import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Paper,
  Select,
  MenuItem,
  TextField,
  Popover,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ContactPhone,
  FileUpload,
  FileDownload,
  Add,
  Star,
  StarBorder,
  Delete,
  TuneOutlined,
  Sync,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  getAdminPhoneCatalog,
  createPhoneCatalogEntry,
  updatePhoneCatalogEntry,
  deletePhoneCatalogEntry,
  togglePhoneCatalogTop,
  togglePhoneCatalogFreeForTrial,
  exportPhoneCatalog,
  getPhoneCatalogCountries,
  getPhoneCatalogProviders,
  getPhoneCatalogCities,
  bulkDeletePhoneCatalog,
  bulkSetTopPhoneCatalog,
  bulkSetFreeForTrialPhoneCatalog,
  bulkSetCurrencyPhoneCatalog,
  bulkSetFieldPhoneCatalog,
  syncDIDWWRequirements,
  getDIDWWStatus,
} from '../services/phoneCatalogApi';
import PhoneCatalogFilters from '../components/admin/PhoneCatalogFilters';
import PhoneCatalogTable from '../components/admin/PhoneCatalogTable';
import PhoneCatalogForm from '../components/admin/PhoneCatalogForm';
import CsvImportDialog from '../components/admin/CsvImportDialog';

const ITEMS_PER_PAGE = 50;

const AdminPhoneCatalogPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Data
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [providers, setProviders] = useState([]);
  const [cities, setCities] = useState([]);

  // Filters & pagination
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    provider: '',
    search: '',
    top_only: false,
    free_for_trial_only: false,
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formOpen, setFormOpen] = useState(false);
  const [formEntry, setFormEntry] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, entry: null });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkValuesAnchor, setBulkValuesAnchor] = useState(null);
  const [bulkValues, setBulkValues] = useState({
    activation_fee: '',
    monthly_fee: '',
    internal_activation_fee: '',
    internal_monthly_fee: '',
  });
  const [didwwSyncing, setDidwwSyncing] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch reference data
  useEffect(() => {
    const fetchRefs = async () => {
      try {
        const [countriesRes, providersRes, citiesRes] = await Promise.all([
          getPhoneCatalogCountries(),
          getPhoneCatalogProviders(),
          getPhoneCatalogCities(),
        ]);
        setCountries(countriesRes.data?.data || []);
        setProviders(providersRes.data?.data || []);
        setCities(citiesRes.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch reference data:', err);
      }
    };
    fetchRefs();
  }, []);

  // Fetch catalog
  const fetchCatalog = useCallback(async () => {
    setSelectedIds([]);
    setLoading(true);
    try {
      const params = {
        page,
        limit: ITEMS_PER_PAGE,
      };
      if (filters.country) params.country = filters.country;
      if (filters.city) params.city = filters.city;
      if (filters.provider) params.provider = filters.provider;
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.top_only) params.top_only = true;
      if (filters.free_for_trial_only) params.free_for_trial_only = true;

      const res = await getAdminPhoneCatalog(params);
      const payload = res.data;
      setEntries(payload?.data || []);
      setTotal(payload?.total || 0);
      setTotalPages(payload?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch catalog:', err);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.fetchError', 'Failed to load catalog'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, filters.country, filters.city, filters.provider, debouncedSearch, filters.top_only, filters.free_for_trial_only, t]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Handlers
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field !== 'search') setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ country: '', city: '', provider: '', search: '', top_only: false, free_for_trial_only: false });
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      if (sortOrder === 'asc') setSortOrder('desc');
      else if (sortOrder === 'desc') {
        setSortField('');
        setSortOrder('');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleInlineEdit = async (id, updates) => {
    try {
      await updatePhoneCatalogEntry(id, updates);
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, ...updates } : e))
      );
      setSnackbar({
        open: true,
        message: t('phoneCatalog.updated', 'Entry updated'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to update entry:', err);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.updateError', 'Failed to update entry'),
        severity: 'error',
      });
    }
  };

  const handleToggleTop = async (id) => {
    try {
      await togglePhoneCatalogTop(id);
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, is_top_country: !e.is_top_country } : e
        )
      );
    } catch (err) {
      console.error('Failed to toggle top:', err);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.toggleError', 'Failed to toggle top status'),
        severity: 'error',
      });
    }
  };

  const handleToggleFreeForTrial = async (id) => {
    try {
      await togglePhoneCatalogFreeForTrial(id);
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, is_free_for_trial: !e.is_free_for_trial } : e
        )
      );
    } catch (err) {
      console.error('Failed to toggle free-for-trial:', err);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.toggleFreeForTrialError', 'Failed to toggle free-for-trial status'),
        severity: 'error',
      });
    }
  };

  const handleFormSave = async (payload, existingId) => {
    if (existingId) {
      await updatePhoneCatalogEntry(existingId, payload);
      setEntries((prev) =>
        prev.map((e) => (e.id === existingId ? { ...e, ...payload } : e))
      );
      setSnackbar({
        open: true,
        message: t('phoneCatalog.updated', 'Entry updated'),
        severity: 'success',
      });
    } else {
      await createPhoneCatalogEntry(payload);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.created', 'Entry created'),
        severity: 'success',
      });
      fetchCatalog();
    }
  };

  const handleDelete = async () => {
    const entry = deleteDialog.entry;
    if (!entry) return;
    try {
      await deletePhoneCatalogEntry(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      setTotal((prev) => prev - 1);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.deleted', 'Entry deleted'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to delete entry:', err);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.deleteError', 'Failed to delete entry'),
        severity: 'error',
      });
    } finally {
      setDeleteDialog({ open: false, entry: null });
    }
  };

  const handleBulkSetTop = async () => {
    try {
      await bulkSetTopPhoneCatalog(selectedIds, true);
      setEntries((prev) =>
        prev.map((e) => (selectedIds.includes(e.id) ? { ...e, is_top_country: true } : e))
      );
      setSnackbar({ open: true, message: `${selectedIds.length} entries set as top country`, severity: 'success' });
      setSelectedIds([]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleBulkUnsetTop = async () => {
    try {
      await bulkSetTopPhoneCatalog(selectedIds, false);
      setEntries((prev) =>
        prev.map((e) => (selectedIds.includes(e.id) ? { ...e, is_top_country: false } : e))
      );
      setSnackbar({ open: true, message: `${selectedIds.length} entries unset from top country`, severity: 'success' });
      setSelectedIds([]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleBulkSetFreeForTrial = async () => {
    try {
      await bulkSetFreeForTrialPhoneCatalog(selectedIds, true);
      setEntries((prev) =>
        prev.map((e) => (selectedIds.includes(e.id) ? { ...e, is_free_for_trial: true } : e))
      );
      setSnackbar({ open: true, message: `${selectedIds.length} entries marked as free for trial`, severity: 'success' });
      setSelectedIds([]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleBulkUnsetFreeForTrial = async () => {
    try {
      await bulkSetFreeForTrialPhoneCatalog(selectedIds, false);
      setEntries((prev) =>
        prev.map((e) => (selectedIds.includes(e.id) ? { ...e, is_free_for_trial: false } : e))
      );
      setSnackbar({ open: true, message: `${selectedIds.length} entries unmarked as free for trial`, severity: 'success' });
      setSelectedIds([]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleBulkSetCurrency = async (currency) => {
    if (!currency) return;
    try {
      await bulkSetCurrencyPhoneCatalog(selectedIds, currency);
      setEntries((prev) =>
        prev.map((e) => (selectedIds.includes(e.id) ? { ...e, currency } : e))
      );
      setSnackbar({ open: true, message: `Currency set to ${currency} for ${selectedIds.length} entries`, severity: 'success' });
      setSelectedIds([]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update currency', severity: 'error' });
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeletePhoneCatalog(selectedIds);
      setEntries((prev) => prev.filter((e) => !selectedIds.includes(e.id)));
      setTotal((prev) => prev - selectedIds.length);
      setSnackbar({ open: true, message: `${selectedIds.length} entries deleted`, severity: 'success' });
      setSelectedIds([]);
      setBulkDeleteDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
      setBulkDeleteDialog(false);
    }
  };

  const handleBulkApplyValues = async () => {
    const fieldsToUpdate = Object.entries(bulkValues)
      .filter(([, v]) => v !== '')
      .map(([field, v]) => [field, parseFloat(v)]);
    if (fieldsToUpdate.length === 0) return;
    try {
      for (const [field, value] of fieldsToUpdate) {
        await bulkSetFieldPhoneCatalog(selectedIds, field, value);
      }
      const updates = Object.fromEntries(fieldsToUpdate);
      setEntries((prev) =>
        prev.map((e) => (selectedIds.includes(e.id) ? { ...e, ...updates } : e))
      );
      setSnackbar({ open: true, message: `Updated ${fieldsToUpdate.length} field(s) for ${selectedIds.length} entries`, severity: 'success' });
      setSelectedIds([]);
      setBulkValues({ activation_fee: '', monthly_fee: '', internal_activation_fee: '', internal_monthly_fee: '' });
      setBulkValuesAnchor(null);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportPhoneCatalog();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'phone_catalog.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
      setSnackbar({
        open: true,
        message: t('phoneCatalog.exportError', 'Failed to export catalog'),
        severity: 'error',
      });
    }
  };

  const handleDIDWWSync = async () => {
    setDidwwSyncing(true);
    try {
      await syncDIDWWRequirements();
      setSnackbar({
        open: true,
        message: t('admin.syncDIDWWStarted', 'DIDWW sync started, please wait...'),
        severity: 'info',
      });
      // Poll status until completed or failed
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await getDIDWWStatus();
          const syncInfo = statusRes.data?.sync;
          if (!syncInfo || syncInfo.status === 'completed' || syncInfo.status === 'failed') {
            clearInterval(pollInterval);
            setDidwwSyncing(false);
            if (syncInfo?.status === 'completed') {
              const errCount = syncInfo.errors?.length || 0;
              setSnackbar({
                open: true,
                message: t('admin.syncDIDWWSuccess', 'Synced {{countries}} countries, updated {{entries}} entries', {
                  countries: syncInfo.synced_countries,
                  entries: syncInfo.updated_catalog_entries,
                }) + (errCount > 0 ? ` (${errCount} errors)` : '') + ` (${syncInfo.duration_seconds.toFixed(1)}s)`,
                severity: errCount > 0 ? 'warning' : 'success',
              });
              fetchCatalog();
            } else {
              setSnackbar({
                open: true,
                message: t('admin.syncDIDWWError', 'Failed to sync DIDWW requirements'),
                severity: 'error',
              });
            }
          }
        } catch {
          clearInterval(pollInterval);
          setDidwwSyncing(false);
        }
      }, 3000);
    } catch (err) {
      console.error('DIDWW sync failed:', err);
      const errMsg = err.response?.status === 409
        ? t('admin.syncDIDWWAlreadyRunning', 'Sync already in progress')
        : t('admin.syncDIDWWError', 'Failed to sync DIDWW requirements');
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
      setDidwwSyncing(false);
    }
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Toolbar>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            {t('common.backToCalculator', 'Back to Calculator')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContactPhone sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {t('phoneCatalog.title', 'Phone Catalog')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ p: 4 }}>
        {/* Page header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {t('phoneCatalog.title', 'Phone Catalog')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('phoneCatalog.subtitle', 'Manage phone number pricing catalog')}
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<FileUpload />}
              onClick={handleImportClick}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#00C853',
                '&:hover': { bgcolor: '#00A843' },
                borderRadius: 1,
              }}
            >
              {t('phoneCatalog.importCsv', 'Import CSV')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setFormEntry(null);
                setFormOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#00C853',
                color: '#00C853',
                '&:hover': { borderColor: '#00A843', bgcolor: 'rgba(0,200,83,0.04)' },
                borderRadius: 1,
              }}
            >
              {t('phoneCatalog.addNumber', 'Add Number')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#00C853',
                color: '#00C853',
                '&:hover': { borderColor: '#00A843', bgcolor: 'rgba(0,200,83,0.04)' },
                borderRadius: 1,
              }}
            >
              {t('phoneCatalog.exportCsv', 'Export CSV')}
            </Button>
            <Button
              variant="outlined"
              startIcon={didwwSyncing ? <CircularProgress size={16} /> : <Sync />}
              onClick={handleDIDWWSync}
              disabled={didwwSyncing}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': { borderColor: '#1565c0', bgcolor: 'rgba(25,118,210,0.04)' },
                borderRadius: 1,
              }}
            >
              {didwwSyncing
                ? t('admin.syncDIDWWSyncing', 'Syncing...')
                : t('admin.syncDIDWW', 'Sync DIDWW Docs')}
            </Button>

            <Box sx={{ flex: 1 }} />

            <Chip
              label={t('phoneCatalog.showingEntries', 'Showing {{showing}} of {{total}} entries', {
                showing: entries.length,
                total,
              })}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: 12,
                bgcolor: 'white',
                border: '1px solid #e5e7eb',
              }}
            />
          </Box>
        </Box>

        {/* Filters */}
        <PhoneCatalogFilters
          countries={countries}
          cities={cities}
          providers={providers}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Bulk Actions Bar */}
        <Collapse in={selectedIds.length > 0}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 1,
              bgcolor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {selectedIds.length} {t('phoneCatalog.entriesSelected', 'entries selected')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<TuneOutlined />}
              onClick={(e) => setBulkValuesAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', borderColor: '#6b7280', color: '#374151', borderRadius: 1, fontWeight: 500 }}
            >
              {t('phoneCatalog.setValues', 'Set Values')}
            </Button>
            <Select
              size="small"
              value=""
              displayEmpty
              onChange={(e) => handleBulkSetCurrency(e.target.value)}
              sx={{ minWidth: 120, height: 32, borderRadius: 1, fontSize: 13 }}
            >
              <MenuItem value="" disabled><em>{t('phoneCatalog.setCurrency', 'Set Currency')}</em></MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="UAH">UAH</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Star />}
              onClick={handleBulkSetTop}
              sx={{ textTransform: 'none', borderColor: '#00C853', color: '#00C853', borderRadius: 1 }}
            >
              {t('phoneCatalog.setTop', 'Set Top')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<StarBorder />}
              onClick={handleBulkUnsetTop}
              sx={{ textTransform: 'none', borderColor: '#f59e0b', color: '#f59e0b', borderRadius: 1 }}
            >
              {t('phoneCatalog.unsetTop', 'Unset Top')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleBulkSetFreeForTrial}
              sx={{ textTransform: 'none', borderColor: '#00C853', color: '#00C853', borderRadius: 1 }}
            >
              {t('phoneCatalog.setFreeForTrial', 'Set Free trial')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleBulkUnsetFreeForTrial}
              sx={{ textTransform: 'none', borderColor: '#f59e0b', color: '#f59e0b', borderRadius: 1 }}
            >
              {t('phoneCatalog.unsetFreeForTrial', 'Unset Free trial')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setBulkDeleteDialog(true)}
              sx={{ textTransform: 'none', borderRadius: 1 }}
            >
              {t('common.delete', 'Delete')}
            </Button>
          </Paper>
        </Collapse>

        {/* Set Values Popover */}
        <Popover
          open={Boolean(bulkValuesAnchor)}
          anchorEl={bulkValuesAnchor}
          onClose={() => setBulkValuesAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 2.5, borderRadius: 1, minWidth: 320 } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('phoneCatalog.bulkSetValues', 'Bulk Set Values')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              { key: 'activation_fee', label: t('phoneCatalog.activationFee', 'Activation Fee') },
              { key: 'monthly_fee', label: t('phoneCatalog.monthlyFee', 'Monthly Fee') },
              { key: 'internal_activation_fee', label: t('phoneCatalog.intActivation', 'Int. Activation Fee') },
              { key: 'internal_monthly_fee', label: t('phoneCatalog.intMonthly', 'Int. Monthly Fee') },
            ].map(({ key, label }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" sx={{ width: 130, fontSize: 13, color: 'text.secondary' }}>
                  {label}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  placeholder="0.00"
                  value={bulkValues[key]}
                  onChange={(e) => setBulkValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 32, borderRadius: 1, fontSize: 13 } }}
                />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5, pt: 1.5, borderTop: '1px solid #e5e7eb' }}>
            <Button
              size="small"
              onClick={() => {
                setBulkValuesAnchor(null);
                setBulkValues({ activation_fee: '', monthly_fee: '', internal_activation_fee: '', internal_monthly_fee: '' });
              }}
              sx={{ textTransform: 'none', color: 'text.secondary', borderRadius: 1 }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleBulkApplyValues}
              disabled={!Object.values(bulkValues).some((v) => v !== '')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#00C853',
                '&:hover': { bgcolor: '#00A843' },
                borderRadius: 1,
              }}
            >
              {t('phoneCatalog.apply', 'Apply')}
            </Button>
          </Box>
        </Popover>

        {/* Table */}
        <PhoneCatalogTable
          entries={entries}
          loading={loading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onInlineEdit={handleInlineEdit}
          onToggleTop={handleToggleTop}
          onToggleFreeForTrial={handleToggleFreeForTrial}
          onEditClick={(entry) => {
            setFormEntry(entry);
            setFormOpen(true);
          }}
          onDeleteClick={(entry) => setDeleteDialog({ open: true, entry })}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Box>

      {/* Add/Edit Form Dialog */}
      <PhoneCatalogForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setFormEntry(null);
        }}
        onSave={handleFormSave}
        entry={formEntry}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, entry: null })}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('phoneCatalog.confirmDelete', 'Delete Entry?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'phoneCatalog.confirmDeleteText',
              'Are you sure you want to delete {{country}} — {{city}} ({{code}})?',
              {
                country: deleteDialog.entry?.country || '',
                city: deleteDialog.entry?.city || '',
                code: deleteDialog.entry?.code || '',
              }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, entry: null })}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete {selectedIds.length} Entries?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedIds.length} selected entries? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setBulkDeleteDialog(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete {selectedIds.length} Entries
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <CsvImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={() => fetchCatalog()}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPhoneCatalogPage;
